import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const STORAGE_CURRENCY = "ie:currency";
const STORAGE_INR_REF = "ie:show-inr-ref";
const STORAGE_RATES = "ie:fx-rates";
const STORAGE_META = "ie:fx-meta";
const RATE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const CurrencyContext = createContext(null);

const FALLBACK_META = {
  INR: { symbol: "₹", flag: "🇮🇳", name: "Indian Rupee", locale: "en-IN" },
  USD: { symbol: "$", flag: "🇺🇸", name: "US Dollar", locale: "en-US" },
  EUR: { symbol: "€", flag: "🇪🇺", name: "Euro", locale: "en-GB" },
  GBP: { symbol: "£", flag: "🇬🇧", name: "British Pound", locale: "en-GB" },
  AED: { symbol: "AED", flag: "🇦🇪", name: "UAE Dirham", locale: "en-AE" },
  SGD: { symbol: "S$", flag: "🇸🇬", name: "Singapore Dollar", locale: "en-SG" },
  AUD: { symbol: "A$", flag: "🇦🇺", name: "Australian Dollar", locale: "en-AU" },
  CAD: { symbol: "C$", flag: "🇨🇦", name: "Canadian Dollar", locale: "en-CA" },
  ZAR: { symbol: "R", flag: "🇿🇦", name: "South African Rand", locale: "en-ZA" },
};
const FALLBACK_RATES = {
  INR: 1.0, USD: 0.01201, EUR: 0.01105, GBP: 0.00953, AED: 0.04413,
  SGD: 0.01614, AUD: 0.01836, CAD: 0.01647, ZAR: 0.22301,
};

// Timezone → currency lookup for browser-side auto-detect (backup for when
// backend header-based detection returns INR because there is no CDN header).
const TZ_TO_CURRENCY = {
  "America/": "USD", "US/": "USD",
  "Europe/London": "GBP", "Europe/Dublin": "EUR", "Europe/Berlin": "EUR",
  "Europe/Paris": "EUR", "Europe/Madrid": "EUR", "Europe/Rome": "EUR",
  "Europe/Amsterdam": "EUR", "Europe/Brussels": "EUR", "Europe/Vienna": "EUR",
  "Asia/Dubai": "AED", "Asia/Riyadh": "AED", "Asia/Kuwait": "AED",
  "Asia/Qatar": "AED", "Asia/Bahrain": "AED", "Asia/Muscat": "AED",
  "Asia/Singapore": "SGD", "Asia/Kuala_Lumpur": "SGD", "Asia/Bangkok": "SGD",
  "Asia/Jakarta": "SGD", "Asia/Manila": "SGD", "Asia/Ho_Chi_Minh": "SGD",
  "Australia/": "AUD", "Pacific/Auckland": "AUD",
  "America/Toronto": "CAD", "America/Vancouver": "CAD", "America/Montreal": "CAD",
  "Africa/Johannesburg": "ZAR", "Africa/Cape_Town": "ZAR",
  "Asia/Kolkata": "INR", "Asia/Calcutta": "INR",
};

function tzGuessCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    for (const [prefix, cur] of Object.entries(TZ_TO_CURRENCY)) {
      if (tz === prefix || tz.startsWith(prefix)) return cur;
    }
  } catch { /* ignore */ }
  return "INR";
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState("INR");
  const [showInrRef, setShowInrRefState] = useState(true);
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [meta, setMeta] = useState(FALLBACK_META);
  const [ready, setReady] = useState(false);

  // Load persisted preferences + cached rates on mount.
  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem(STORAGE_CURRENCY);
      const savedInrRef = localStorage.getItem(STORAGE_INR_REF);
      const cachedRates = JSON.parse(localStorage.getItem(STORAGE_RATES) || "null");
      const cachedMeta = JSON.parse(localStorage.getItem(STORAGE_META) || "null");
      if (cachedRates?.rates && (Date.now() - (cachedRates.savedAt || 0)) < RATE_TTL_MS) {
        setRates(cachedRates.rates);
      }
      if (cachedMeta) setMeta(cachedMeta);
      if (savedCurrency && FALLBACK_RATES[savedCurrency]) {
        setCurrencyState(savedCurrency);
      }
      if (savedInrRef !== null) setShowInrRefState(savedInrRef === "true");
    } catch { /* ignore */ }
    setReady(true);
  }, []);

  // Fetch fresh rates once we know user's saved prefs (or lack thereof).
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/currency/rates");
        if (!alive) return;
        if (res.data?.rates) {
          setRates(res.data.rates);
          localStorage.setItem(STORAGE_RATES, JSON.stringify({ rates: res.data.rates, savedAt: Date.now() }));
        }
        if (res.data?.meta) {
          setMeta(res.data.meta);
          localStorage.setItem(STORAGE_META, JSON.stringify(res.data.meta));
        }
        // Auto-detect only if the user has NOT explicitly picked a currency before.
        if (!localStorage.getItem(STORAGE_CURRENCY)) {
          let detected = "INR";
          try {
            const det = await api.get("/currency/detect");
            if (det.data?.currency && FALLBACK_RATES[det.data.currency]) detected = det.data.currency;
          } catch { /* ignore */ }
          if (detected === "INR") detected = tzGuessCurrency();
          if (alive && detected && detected !== "INR" && FALLBACK_RATES[detected]) {
            setCurrencyState(detected);
          }
        }
      } catch { /* keep fallbacks */ }
    })();
    return () => { alive = false; };
  }, [ready]);

  const setCurrency = useCallback((code) => {
    if (!FALLBACK_RATES[code]) return;
    setCurrencyState(code);
    try { localStorage.setItem(STORAGE_CURRENCY, code); } catch { /* ignore */ }
  }, []);

  const setShowInrRef = useCallback((v) => {
    setShowInrRefState(!!v);
    try { localStorage.setItem(STORAGE_INR_REF, v ? "true" : "false"); } catch { /* ignore */ }
  }, []);

  const convert = useCallback((inrAmount, targetCurrency = null) => {
    const target = targetCurrency || currency;
    const rate = rates[target] || FALLBACK_RATES[target] || 1;
    return Number(inrAmount || 0) * rate;
  }, [currency, rates]);

  const format = useCallback((amount, targetCurrency = null) => {
    const target = targetCurrency || currency;
    const m = meta[target] || FALLBACK_META[target] || {};
    const locale = m.locale || "en-US";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: target,
        maximumFractionDigits: target === "INR" ? 0 : 2,
        minimumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${m.symbol || target} ${Math.round(amount).toLocaleString()}`;
    }
  }, [currency, meta]);

  const formatFromInr = useCallback((inrAmount, targetCurrency = null) => {
    const target = targetCurrency || currency;
    return format(convert(inrAmount, target), target);
  }, [convert, format, currency]);

  const value = useMemo(() => ({
    currency, setCurrency, showInrRef, setShowInrRef,
    rates, meta, ready, convert, format, formatFromInr,
    supported: Object.keys(FALLBACK_RATES),
  }), [currency, showInrRef, rates, meta, ready, setCurrency, setShowInrRef, convert, format, formatFromInr]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within <CurrencyProvider>");
  return ctx;
}
